/**
 * Spine — ordered collection of Sections making up the reading order.
 */
import { Section } from "./section";
import type { PackagingResult } from "./packaging";

export class Spine {
  items: Section[] = [];
  private byHref: Record<string, Section> = {};
  private byIdref: Record<string, Section> = {};

  /**
   * Build spine from parsed packaging + path resolver.
   * @param packaging Parsed OPF data
   * @param resolver Resolves relative href to absolute URL
   * @param canonical Optional canonical URL function
   */
  unpack(
    packaging: PackagingResult,
    resolver: (href: string) => string,
    canonical?: (href: string) => string,
  ): void {
    const { spine, manifest, spineNodeIndex } = packaging;

    for (let i = 0; i < spine.length; i++) {
      const entry = spine[i];
      const manifestItem = manifest[entry.idref];
      if (!manifestItem) continue;

      const href = manifestItem.href;
      const url = resolver(href);
      const canon = canonical ? canonical(href) : url;

      // Generate CFI base: /{spineStep}/{itemStep}[idref]
      // spineStep = (spineNodeIndex + 1) * 2 — the CFI step to reach <spine> within <package>
      // itemStep = (i + 1) * 2 — the CFI step to reach the <itemref> within <spine>
      // This matches epub.js generateChapterComponent(spineNodeIndex, item.index, item.id)
      const spineStep = (spineNodeIndex + 1) * 2;
      const itemStep = (i + 1) * 2;
      const idPart = entry.idref ? `[${entry.idref}]` : "";
      const cfiBase = `/${spineStep}/${itemStep}${idPart}`;

      const section = new Section({
        idref: entry.idref,
        linear: entry.linear,
        properties: [...entry.properties, ...manifestItem.properties],
        index: i,
        href,
        url,
        canonical: canon,
        cfiBase,
      });

      this.items.push(section);
      this.indexSection(section);
    }

    // Wire up prev/next (skip non-linear)
    for (let i = 0; i < this.items.length; i++) {
      const section = this.items[i];
      section.setPrev(() => {
        for (let j = i - 1; j >= 0; j--) {
          if (this.items[j].linear) return this.items[j];
        }
        return null;
      });
      section.setNext(() => {
        for (let j = i + 1; j < this.items.length; j++) {
          if (this.items[j].linear) return this.items[j];
        }
        return null;
      });
    }
  }

  private indexSection(section: Section): void {
    const href = section.href;
    this.byHref[href] = section;
    this.byHref[encodeURI(href)] = section;
    this.byHref[decodeURI(href)] = section;
    this.byIdref[section.idref] = section;
  }

  /**
   * Get a section by index, href, or idref.
   */
  get(target?: number | string): Section | null {
    if (target === undefined || target === null) {
      return this.first();
    }
    if (typeof target === "number") {
      return this.items[target] ?? null;
    }
    // Try by href
    const byHref = this.byHref[target] ?? this.byHref[decodeURI(target)];
    if (byHref) return byHref;
    // Try by href without fragment
    const noFrag = target.split("#")[0];
    if (noFrag !== target) {
      const found = this.byHref[noFrag] ?? this.byHref[decodeURI(noFrag)];
      if (found) return found;
    }
    // Try by idref
    if (this.byIdref[target]) return this.byIdref[target];
    // Try by # prefix id
    if (target.startsWith("#")) {
      return this.byIdref[target.slice(1)] ?? null;
    }
    return null;
  }

  first(): Section | null {
    for (const s of this.items) {
      if (s.linear) return s;
    }
    return this.items[0] ?? null;
  }

  last(): Section | null {
    for (let i = this.items.length - 1; i >= 0; i--) {
      if (this.items[i].linear) return this.items[i];
    }
    return this.items[this.items.length - 1] ?? null;
  }

  each(fn: (section: Section, index: number) => void): void {
    this.items.forEach(fn);
  }

  destroy(): void {
    for (const s of this.items) s.destroy();
    this.items = [];
    this.byHref = {};
    this.byIdref = {};
  }
}
